/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import clsx from "clsx";
import { colors } from "themes";

interface HeaderButtonsProps {
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  color?: string;
  variant?: "primary" | "secondary" | "tertiary" | "selected" | "inactive";
  onClick?: () => void;
  size?: "small" | "medium" | "large";
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  loading?: boolean;
  fullWidth?: boolean;
}

const sizeClasses = {
  small: "px-3 py-1.5 text-sm",
  medium: "px-3 py-2.5 text-sm",
  large: "px-6 py-3 text-base",
};

const getVariantStyles = (variant: "primary" | "secondary" | "tertiary" | "selected" | "inactive") => {
  switch (variant) {
    case "primary":
      return {
        backgroundColor: colors.primary,
        color: colors.white,
      };
    case "secondary":
      return {
        backgroundColor: colors.gray,
        color: colors.white,
      };
    case "tertiary":
      return {
        backgroundColor: colors.white,
        color: colors.slate,
        border: "1px solid #D1D5DB",
      };
    case "selected":
      return {
        backgroundColor: colors.selectedBlue,
        color: colors.white,
      };
    case "inactive":
      // We must return an empty object here so Tailwind can control the Light/Dark switch
      return {};
    default:
      return {
        backgroundColor: colors.primary,
        color: colors.white,
      };
  }
};

const variantClasses = {
  primary: "hover:opacity-90 ",
  secondary: "hover:opacity-90",
  tertiary: "hover:bg-gray-100 dark:hover:bg-gray-700 ",
  selected: "hover:opacity-90 ",
  // Here we exactly replicate the tertiary styles for light mode using your theme colors
  // Light Mode: bg-white, text-[#334155] (colors.slate), border-[#D1D5DB]
  // Dark Mode: dark:bg-gray-700, dark:text-white, dark:border-gray-600
  inactive: "bg-white text-[#334155] border border-[#D1D5DB] hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600",
};

const HeaderButtons: React.FC<HeaderButtonsProps> = ({
  type = "button",
  disabled = false,
  children,
  color = "blue",
  className = "",
  size = "medium",
  variant = "primary",
  onClick,
  loading = false,
  icon,
  iconPosition = "left",
  fullWidth = false,
  ...rest
}) => {
  const isDisabled = disabled || loading;
  const variantStyles = getVariantStyles(variant);

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      style={variantStyles}
      {...rest}
      className={clsx(
        "inline-flex items-center justify-center font-bold rounded-lg  disabled:cursor-not-allowed disabled:opacity-50 transition",
        variantClasses[variant],
        sizeClasses[size],
        {
          "w-full": fullWidth,
        },
        className
      )}
    >
      {loading && (
        <svg
          className={clsx(
            "animate-spin mr-2 h-5 w-5",
            iconPosition === "right" && "order-2 ml-2"
          )}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
          ></path>
        </svg>
      )}
      {icon && iconPosition === "left" && <span className="mr-2">{icon}</span>}
      <span>{children}</span>
      {icon && iconPosition === "right" && <span className="ml-2">{icon}</span>}
    </button>
  );
};

export default HeaderButtons;